import {
  AccessList,
  AggregatedStatus,
  Device,
  Feature,
  GatewayType,
  Installation,
  TargetRealmType,
  VentilationQuickSelectionsCommands,
  VentilationQuickSelectionsProperties,
  _Action,
  _Link,
  ViError,
} from '@vi-iot/client';

/**
 * This is a list of available gateway types and matching platform supported by DC
 * Source: https://wiki.viessmann-climatesolutions.com/display/DCC/Regex+for+identifying+gateway-type+from+serial
 */
export enum Platform {
  E3 = 'E3',
  Legacy = 'LEGACY',
  None = 'None',
}

export enum InstallationType {
  Commercial = 'Commercial',
  Residential = 'Residential',
}

export enum NormalOperatingProgramType {
  Normal = 'normal',
  NormalHeating = 'normalHeating',
}

export enum BufferTankOperatingModeType {
  Heating = 'heating',
  Cooling = 'cooling',
}

export enum OnOffEnum {
  On = 'On',
  Off = 'Off',
}

export const FEATURES_PANEL_TYPES = {
  System: 'System',
  CommunicationModule: 'Communication Module',
  NotificationTile: 'Notification Tile',
  Dhw: 'Dhw',
  FuelCell: 'Fuel Cell',
  Solar: 'Solar',
  HeatingCircuit0: 'Heating Circuit 0',
  HeatingCircuit1: 'Heating Circuit 1',
  HeatingCircuit2: 'Heating Circuit 2',
  HeatingCircuit3: 'Heating Circuit 3',
  HeatingZone1: 'Heating Zone 1',
  HeatingZone2: 'Heating Zone 2',
  HeatingZone3: 'Heating Zone 3',
  HeatingZone: 'Heating Zone',
  Ventilation: 'Ventilation',
  ElectricHeater: 'Electric Heater',
  RefrigerantCircuit0: 'Refrigerant Circuit 0',
  RefrigerantCircuit1: 'Refrigerant Circuit 1',
  RefrigerantCircuit2: 'Refrigerant Circuit 2',
  RefrigerantCircuit3: 'Refrigerant Circuit 3',
  SingleRoomControl: 'Single Room Control',
  Battery: 'Battery',
  Photovoltaics: 'Photovoltaics',
  GasConsumption: 'Gas Consumption',
  PowerProduction: 'Power Production',
  SolarPowerProduction: 'Solar Power Production',
  ElectricConsumption: 'Electric Consumption',
  FuelConsumption: 'Fuel Consumption',
  HeatProduction: 'Heat Production',
  Autarky: 'Autarky',
  EnergyCockpit: 'Energy Cockpit',
  EcoWater: 'Ecowater',
  BufferTank: 'Buffer Tank',
  RemoteController: 'Remote Controller',
  WallboxSystem: 'Wallbox System',
  WallboxChargingMode: 'Wallbox charging mode',
  ViCarePlans: 'ViCare Plans',
  ViCareThermostat: 'ViCare Thermostat',
  SummaryGasConsumption: 'Summary Gas Consumption',
  SummaryElectricityConsumption: 'Summary Electricity Consumption',
  BatteryOverall: 'Battery overall',
  PhotovoltaicOverall: 'Photovoltaic overall',
  InternalHydraulic: 'Internal Hydraulic',
  HybridProControl: 'Hybrid Pro Control',
};

export interface PanelsOrderState {
  value: InstallationDevicePanels;
  loaded: boolean;
  error: ViError;
}
export interface InstallationDevicePanels {
  [key: string]: InstallationPanelOrder[];
}
export interface InstallationPanelOrder {
  panel: string;
}

export const defaultPanelsOrder: InstallationPanelOrder[] = [
  {
    // order 1
    panel: FEATURES_PANEL_TYPES.System,
  },
  {
    // order 2
    panel: FEATURES_PANEL_TYPES.CommunicationModule,
  },
  {
    // order 3
    panel: FEATURES_PANEL_TYPES.NotificationTile,
  },
  {
    // order 4
    panel: FEATURES_PANEL_TYPES.Dhw,
  },
  {
    // order 5
    panel: FEATURES_PANEL_TYPES.Solar,
  },
  {
    // order 6
    panel: FEATURES_PANEL_TYPES.FuelCell,
  },
  {
    // order 7
    panel: FEATURES_PANEL_TYPES.HeatingZone1,
  },
  {
    // order 8
    panel: FEATURES_PANEL_TYPES.HeatingZone2,
  },
  {
    // order 9
    panel: FEATURES_PANEL_TYPES.HeatingZone3,
  },
  {
    // order 10
    panel: FEATURES_PANEL_TYPES.HeatingZone,
  },
  {
    // order 11
    panel: FEATURES_PANEL_TYPES.HeatingCircuit0,
  },
  {
    // order 12
    panel: FEATURES_PANEL_TYPES.HeatingCircuit1,
  },
  {
    // order 13
    panel: FEATURES_PANEL_TYPES.HeatingCircuit2,
  },
  {
    // order 14
    panel: FEATURES_PANEL_TYPES.HeatingCircuit3,
  },
  {
    // order 15
    panel: FEATURES_PANEL_TYPES.Ventilation,
  },
  {
    // order 16
    panel: FEATURES_PANEL_TYPES.ElectricHeater,
  },
  {
    // order 17
    panel: FEATURES_PANEL_TYPES.RefrigerantCircuit0,
  },
  {
    // order 18
    panel: FEATURES_PANEL_TYPES.RefrigerantCircuit1,
  },
  {
    // order 19
    panel: FEATURES_PANEL_TYPES.RefrigerantCircuit2,
  },
  {
    // order 20
    panel: FEATURES_PANEL_TYPES.RefrigerantCircuit3,
  },
  {
    // order 21
    panel: FEATURES_PANEL_TYPES.Battery,
  },
  {
    // order 22
    panel: FEATURES_PANEL_TYPES.Photovoltaics,
  },
  {
    // order 23
    panel: FEATURES_PANEL_TYPES.GasConsumption,
  },
  {
    // order 24
    panel: FEATURES_PANEL_TYPES.PowerProduction,
  },
  {
    // order 25
    panel: FEATURES_PANEL_TYPES.ElectricConsumption,
  },
  {
    // order 26
    panel: FEATURES_PANEL_TYPES.FuelConsumption,
  },
  {
    // order 27
    panel: FEATURES_PANEL_TYPES.HeatProduction,
  },
  {
    // order 28
    panel: FEATURES_PANEL_TYPES.Autarky,
  },
  {
    // order 29
    panel: FEATURES_PANEL_TYPES.EnergyCockpit,
  },
  {
    // order 30
    panel: FEATURES_PANEL_TYPES.SolarPowerProduction,
  },
  {
    // order 31
    panel: FEATURES_PANEL_TYPES.EcoWater,
  },
  {
    // order 32
    panel: FEATURES_PANEL_TYPES.BufferTank,
  },
  {
    // order 33
    panel: FEATURES_PANEL_TYPES.RemoteController,
  },
  {
    // order 34
    panel: FEATURES_PANEL_TYPES.WallboxSystem,
  },
  {
    // order 35
    panel: FEATURES_PANEL_TYPES.WallboxChargingMode,
  },
  {
    // order 36
    panel: FEATURES_PANEL_TYPES.ViCarePlans,
  },
  {
    // order 37
    panel: FEATURES_PANEL_TYPES.ViCareThermostat,
  },
  {
    // order 38
    panel: FEATURES_PANEL_TYPES.SummaryElectricityConsumption,
  },
  {
    // order 39
    panel: FEATURES_PANEL_TYPES.SummaryGasConsumption,
  },
  {
    // order 40
    panel: FEATURES_PANEL_TYPES.BatteryOverall,
  },
  {
    // order 41
    panel: FEATURES_PANEL_TYPES.PhotovoltaicOverall,
  },
  {
    // order 42
    panel: FEATURES_PANEL_TYPES.InternalHydraulic,
  },
  {
    // order 43
    panel: FEATURES_PANEL_TYPES.HybridProControl,
  },
  // ********************************************
  //      SRC PANEL HAS TO BE THE LAST ONE
  // ********************************************
  {
    // order 44
    panel: FEATURES_PANEL_TYPES.SingleRoomControl,
  },
];

export const partnerShopLinkVkOrgs: string[] = ['0500', '0600', '6100', '6200'];

export interface DeviceDetails {
  marketingName: string;
  shortName: string;
  image: string;
  boilerSerial: string;
  gatewaySerial: string;
  isSelected?: boolean;
  isLoading: boolean;
  id: string;
}

export interface MainDeviceDetails extends DeviceDetails {
  deviceName: string;
  nameVisible: boolean;
}

export interface AddressDetails {
  street: string;
  city: string;
  latitude: number;
  longitude: number;
}

export interface OwnerDetails {
  name: string;
  email: string;
  phone: string;
  ownerId: string;
}

export interface GatewayStatus {
  online: boolean;
  offline: boolean;
  error: boolean;
  remoteDiagnostic: boolean;
  maintenance: boolean;
  worksProperly: boolean;
  nbiot: boolean;
}

export interface GatewayStatusWithChangedAt extends GatewayStatus {
  lastStatusChangedAt: string;
}

export interface InstallationIds {
  installationId: number;
  gatewaySerial: string;
  deviceId: string;
}

export interface Gateway {
  aggregatedStatus: AggregatedStatus;
  autoUpdate: boolean;
  createdAt: string;
  description: string;
  firmwareUpdateFailureCounter: number;
  gatewayType: GatewayType;
  installationId: number;
  lastStatusChangedAt: string;
  producedAt: string;
  registeredAt: string;
  serial: string;
  targetRealm: TargetRealmType;
  version: string;
  devices?: Device[];
  roles: string[];
}

export interface BasicInformationInstallation {
  accessList: AccessList;
  installation: Installation;
}

export interface GatewayMainDeviceDetails {
  id?: string;
  boilerSerial: string | null;
  gatewaySerial: string;
  gatewayType: string;
  marketingName: string;
  deviceImage: string;
  shortDeviceName: string;
  canEditBoilerSerial: boolean;
  maintenancePackageLink: string;
  equipmentNumber: string;
  error?: any;
  loading?: boolean;
}

export interface CascadeLagDeviceDetails {
  id: string;
  name: string;
  type: string;
  burnerStatus: boolean;
}

export interface VentilationQuickSelectionsDetails {
  active: Feature<VentilationQuickSelectionsProperties, VentilationQuickSelectionsCommands>;
  defaultRuntime: number;
}

export enum InstallationHeaderTileTypeName {
  COMMON = 'COMMON',
  ECO_WATER = 'ECO_WATER',
  WALLBOX = 'WALLBOX',
  SOLARLOG = 'SOLARLOG',
  PV_IMMERSION_HEATER = 'PV_IMMERSION_HEATER',
}

export enum GatewayTypeLegacy {
  VitoconnectOpenTherm = 'VitoconnectOpenTherm',
  VitoconnectOptolink = 'VitoconnectOptolink',
  VitoconnectOPTO2 = 'VitoconnectOPTO2',
}

export enum ViCarePlans {
  'basic' = 'Basic',
}

export interface GenericStoreStatus {
  loading: boolean;
  loaded: boolean;
  error: any;
}

export enum DashboardTypes {
  DEFAULT = 'DEFAULT',
  NBIOT = 'NBIOT',
  SOLARLOG = 'SOLARLOG',
  WALLBOX = 'WALLBOX',
  HEMS = 'HEMS',
  PV_IMMERSION_HEATER = 'PV_IMMERSION_HEATER',
}
